import type { ProductCard } from "@itaas/pep-contracts";
import { Link } from "react-router-dom";
import { usePepI18n } from "../../i18n/usePepI18n";
import {
  labelCategory,
  labelStatus,
  productDisplayName,
  productTagline,
  statusBadgeClass,
} from "../../utils/labels";
import { isPrelaunchProduct } from "../../utils/product-status";
import {
  buildConfigureHrefForProduct,
  commercialCtaLabelForProduct,
} from "../../utils/commercial-cta";
import { ProductLogo } from "./ProductLogo";
import { ProductPromotionBadge } from "../commercial/ProductPromotionBadge";

export function ProductCardView({ product }: { product: ProductCard }) {
  const { locale, t } = usePepI18n();
  const tagline = productTagline(product, locale);

  return (
    <article className="card h-100 pep-product-card border-0 shadow-sm">
      <div className="card-body d-flex flex-column">
        <div className="d-flex align-items-start gap-3 mb-3">
          <ProductLogo slug={product.slug} name={productDisplayName(product, locale)} size={52} />
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex justify-content-between align-items-start mb-2 gap-2">
              <span className="badge text-bg-secondary text-uppercase" style={{ fontSize: "0.65rem" }}>
                {labelCategory(product.category, locale)}
              </span>
              <span className={`badge ${statusBadgeClass(product.status)}`}>
                {labelStatus(product.status, locale)}
              </span>
              <ProductPromotionBadge promotion={product.promotion} locale={locale} />
            </div>
            <h3 className="h5 card-title mb-0">{productDisplayName(product, locale)}</h3>
          </div>
        </div>
        <p className="card-text text-secondary small flex-grow-1">
          {tagline.slice(0, 140)}
          {tagline.length > 140 ? "…" : ""}
        </p>
        {product.features.length > 0 && (
          <ul className="list-unstyled small text-secondary mb-3">
            {product.features.slice(0, 3).map((f) => (
              <li key={f}>
                <span className="text-success me-1">✓</span>
                {f}
              </li>
            ))}
          </ul>
        )}
        <div className="d-flex flex-wrap gap-2 align-self-start">
          <Link
            className="btn btn-outline-primary btn-sm"
            to={`/products/${product.slug}`}
          >
            {t("common.learnMore")}
          </Link>
          {product.trialDays &&
            product.status !== "planned" &&
            product.portfolio?.trialAllowed !== false &&
            (product.portfolio?.allowedCtas?.includes("TRIAL") ?? true) && (
            <Link
              className="btn btn-primary btn-sm"
              to={buildConfigureHrefForProduct(product)}
            >
              {commercialCtaLabelForProduct(product, locale)}
            </Link>
          )}
          {isPrelaunchProduct(product.status) && (
            <Link
              className="btn btn-primary btn-sm"
              to={buildConfigureHrefForProduct(product)}
            >
              {locale === "en" ? "Join the waitlist" : "Rejoindre la liste d'attente"}
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}

export function ProductMiniCard({ product }: { product: ProductCard }) {
  const { locale } = usePepI18n();
  const tagline = productTagline(product, locale);

  return (
    <Link
      to={`/products/${product.slug}`}
      className="text-decoration-none d-block p-3 border rounded-3 h-100 pep-mini-card"
    >
      <div className="d-flex align-items-center gap-2 mb-1">
        <ProductLogo slug={product.slug} name={productDisplayName(product, locale)} size={36} />
        <div className="fw-semibold text-body">{productDisplayName(product, locale)}</div>
      </div>
      <div className="small text-secondary">{tagline.slice(0, 80)}…</div>
    </Link>
  );
}
